import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Form,
  Input,
  message,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import api from "../../api/auth";
import { useAuthStore } from "../../store/authStore";
const statusNames: Record<string, string> = {
  queued: "排队中",
  preparing: "准备中",
  running: "构建中",
  succeeded: "成功",
  failed: "失败",
};
const colors: Record<string, string> = {
  queued: "default",
  preparing: "processing",
  running: "processing",
  succeeded: "success",
  failed: "error",
};
const date = (value: string) =>
  value
    ? new Date(value).toLocaleString("zh-CN", { timeZone: "Asia/Shanghai" })
    : "—";
const error = (e: any) =>
  message.error(e.response?.data?.message || e.message || "操作失败");
const BuildJobs: React.FC<{ type: "rpm" | "deb" | "iso" }> = ({ type }) => {
  const { identity } = useAuthStore();
  const admin = identity === "admin";
  const parameterKeys = type === "iso" ? ["app"] : ["edgec", "llcp", "wyos"];
  const visibleParameters = (params: any) =>
    Object.fromEntries(
      Object.entries(params || {}).filter(([key]) =>
        parameterKeys.includes(key)
      )
    );
  const [allowedApps, setAllowedApps] = useState<string[]>([]);
  const [profiles, setProfiles] = useState<any[]>([]),
    [servers, setServers] = useState<any[]>([]),
    [jobs, setJobs] = useState<any[]>([]);
  const [detail, setDetail] = useState<any>(null),
    [log, setLog] = useState(""),
    [loading, setLoading] = useState(false),
    [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1),
    [total, setTotal] = useState(0);
  const [form] = Form.useForm();
  const profileId = Form.useWatch("profileId", form);
  const selected = profiles.find((p) => p.id === profileId);
  const offset = useRef(0),
    selectedId = useRef<string | null>(null),
    fetching = useRef(false);
  const load = useCallback(async () => {
    try {
      const [p, j, apps] = await Promise.all([
        api.get(`/build/${type}/profiles`),
        api.get(`/build/${type}/jobs`, { params: { page, pageSize: 10 } }),
        type === "iso"
          ? api.get("/build/iso/apps")
          : Promise.resolve({ data: [] }),
      ]);
      setProfiles(p.data);
      setJobs(j.data.items);
      setTotal(j.data.total);
      setAllowedApps(apps.data);
    } catch (e) {
      error(e);
    } finally {
      setLoading(false);
    }
  }, [type, page]);
  useEffect(() => {
    setLoading(true);
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);
  useEffect(() => {
    form.resetFields();
    setPage(1);
    setDetail(null);
    selectedId.current = null;
  }, [type, form]);
  useEffect(() => {
    if (type === "iso") {
      const current = form.getFieldValue("app");
      if (!allowedApps.includes(current))
        form.setFieldsValue({ app: allowedApps[0] });
    }
  }, [allowedApps, admin, type, form]);
  useEffect(() => {
    if (admin)
      api
        .get("/admin/build/servers")
        .then((r) => setServers(r.data))
        .catch(error);
  }, [admin]);
  const refreshDetail = useCallback(async () => {
    const id = selectedId.current;
    if (!id || fetching.current) return;
    fetching.current = true;
    try {
      const d = await api.get(`/build/jobs/${id}`);
      if (selectedId.current !== id) return;
      setDetail(d.data);
      try {
        const l = await api.get(`/build/jobs/${id}/log`, {
          params: { offset: offset.current },
          timeout: 35000,
        });
        if (selectedId.current === id) {
          offset.current = l.data.nextOffset;
          setLog((prev) => (prev + l.data.text).slice(-250000));
        }
      } catch (e) {
        if (selectedId.current === id)
          setLog((prev) => prev || "暂时无法读取远程日志，请稍后刷新。\n");
      }
    } catch (e) {
      error(e);
    } finally {
      fetching.current = false;
    }
  }, []);
  useEffect(() => {
    const timer = setInterval(refreshDetail, 10000);
    return () => clearInterval(timer);
  }, [refreshDetail]);
  const open = (job: any) => {
    selectedId.current = job.id;
    offset.current = 0;
    setLog("");
    setDetail(job);
    refreshDetail();
  };
  const submit = async (values: any) => {
    setSubmitting(true);
    try {
      const parameters: any = {};
      if (admin || type === "iso")
        for (const key of parameterKeys)
          if (values[key]) parameters[key] = values[key];
      await api.post(`/build/${type}/jobs`, {
        profileId: values.profileId,
        templateVersionId: selected?.template?.id,
        ...(admin
          ? {
              parameters,
              ...(values.serverId ? { serverId: values.serverId } : {}),
            }
          : type === "iso"
          ? { parameters }
          : {}),
      });
      message.success("任务已进入队列");
      await load();
    } catch (e) {
      error(e);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Typography.Title level={3}>{type.toUpperCase()} 构建</Typography.Title>
      <Card title="提交构建任务">
        {type === "iso" && (
          <Alert
            type="info"
            showIcon
            message="使用同系统、同版本和架构最新成功的包构建成品。提交时固定包来源，ISO 不会触发 RPM/DEB 构建。"
            style={{ marginBottom: 16 }}
          />
        )}
        {!profiles.length && (
          <Alert
            type="info"
            showIcon
            message="暂无可用构建方案，请管理员先配置服务器和默认模板。"
            style={{ marginBottom: 16 }}
          />
        )}
        <Form
          form={form}
          layout="vertical"
          onFinish={submit}
          style={{ maxWidth: 650 }}
        >
          <Form.Item
            name="profileId"
            label="构建方案"
            rules={[{ required: true, message: "请选择构建方案" }]}
          >
            <Select
              options={profiles.map((p) => ({
                value: p.id,
                label: `${p.name} (${p.osFamily} ${p.osVersion} / ${p.architecture})`,
              }))}
            />
          </Form.Item>
          {selected && (
            <Alert
              type="info"
              message={`模板 v${
                selected.template?.versionNo
              } · 默认参数：${JSON.stringify(
                visibleParameters(selected.template?.defaultParameters)
              )}`}
              style={{ marginBottom: 16 }}
            />
          )}
          {admin ? (
            <>
              <Form.Item name="serverId" label="构建服务器">
                <Select
                  allowClear
                  placeholder="自动分配"
                  options={servers
                    .filter((s) => s.enabled)
                    .map((s) => ({ value: s.id, label: s.name }))}
                />
              </Form.Item>
              {(type === "iso" ? [] : parameterKeys).map((key) => (
                <Form.Item key={key} name={key} label={`--${key}`}>
                  <Input
                    placeholder={`留空使用模板默认值${
                      selected?.template?.defaultParameters?.[key]
                        ? "：" + selected.template.defaultParameters[key]
                        : ""
                    }`}
                  />
                </Form.Item>
              ))}
            </>
          ) : null}
          {type === "iso" ? (
            <Form.Item
              name="app"
              label="应用名称 (--app)"
              rules={[{ required: true, message: "请选择频道" }]}
              extra={
                admin
                  ? "可选择所有频道作为应用名称。"
                  : "应用名称仅可使用管理员为你绑定的频道。"
              }
            >
              <Select
                options={allowedApps.map((app) => ({ value: app, label: app }))}
                placeholder={
                  allowedApps.length
                    ? "选择频道"
                    : admin
                    ? "暂无可用频道，请先配置频道"
                    : "尚未绑定频道，请联系管理员"
                }
              />
            </Form.Item>
          ) : null}
          <Button
            htmlType="submit"
            type="primary"
            loading={submitting}
            disabled={
              !profiles.length || (type === "iso" && !allowedApps.length)
            }
          >
            开始构建
          </Button>
        </Form>
      </Card>
      <Card
        title={admin ? "全部构建任务" : "我的构建任务"}
        extra={<Button onClick={load}>刷新</Button>}
      >
        <Table
          rowKey="id"
          dataSource={jobs}
          loading={loading}
          pagination={{
            current: page,
            total,
            pageSize: 10,
            showSizeChanger: false,
            onChange: setPage,
          }}
          scroll={{ x: 850 }}
          columns={[
            {
              title: "任务编号",
              dataIndex: "id",
              render: (id: string) => id.slice(0, 8),
            },
            ...(admin
              ? [{ title: "提交用户", dataIndex: "usernameSnapshot" }]
              : []),
            ...(type === "iso"
              ? [
                  {
                    title: "应用名称",
                    render: (_: any, j: any) => j.effectiveParameters?.app,
                  },
                ]
              : []),
            {
              title: "状态",
              dataIndex: "status",
              render: (s: string) => (
                <Tag color={colors[s]}>{statusNames[s] || s}</Tag>
              ),
            },
            {
              title: "构建方案",
              render: (_: any, j: any) =>
                profiles.find((p) => p.id === j.profileId)?.name ||
                `方案 ${j.profileId}`,
            },
            { title: "提交时间", dataIndex: "createdAt", render: date },
            {
              title: "操作",
              render: (_: any, j: any) => (
                <Space>
                  <Button size="small" onClick={() => open(j)}>
                    详情 / 下载
                  </Button>
                  {["succeeded", "failed"].includes(j.status) && (
                    <Button
                      size="small"
                      onClick={async () => {
                        try {
                          await api.post(`/build/jobs/${j.id}/rebuild`);
                          message.success("已使用当前模板创建新任务");
                          load();
                        } catch (e) {
                          error(e);
                        }
                      }}
                    >
                      重新构建
                    </Button>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Drawer
        width={820}
        title="任务详情"
        open={!!detail}
        onClose={() => {
          selectedId.current = null;
          setDetail(null);
        }}
      >
        {detail && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Descriptions
              bordered
              size="small"
              column={1}
              items={[
                { key: "id", label: "任务编号", children: detail.id },
                {
                  key: "status",
                  label: "状态",
                  children: statusNames[detail.status],
                },
                {
                  key: "server",
                  label: "服务器",
                  children: detail.serverName || "等待分配",
                },
                {
                  key: "params",
                  label: "实际参数",
                  children: JSON.stringify(
                    visibleParameters(detail.effectiveParameters)
                  ),
                },
                {
                  key: "template",
                  label: "模板配置 ID",
                  children: detail.templateVersionId,
                },
                {
                  key: "time",
                  label: "最近状态同步",
                  children: date(detail.lastSyncedAt),
                },
              ]}
            />
            {detail.errorMessage && (
              <Alert
                type={detail.status === "failed" ? "error" : "warning"}
                message={detail.errorMessage}
              />
            )}
            {detail.inputSnapshot && (
              <Card size="small" title="本次构建输入快照">
                <div>固定目录：{detail.inputSnapshot.templatePath}</div>
                <div>读取时间：{date(detail.inputSnapshot.capturedAt)}</div>
                <div>
                  已记录 {detail.inputSnapshot.files.length} 个文件的
                  SHA256，使用任务目录副本构建。
                </div>
              </Card>
            )}
            {detail.packageSnapshot && (
              <Card size="small" title="ISO 使用的包">
                <div>来源任务：{detail.packageSnapshot.sourceJobId}</div>
                <div>
                  包版本：{JSON.stringify(detail.packageSnapshot.versions)}
                </div>
                {detail.packageSnapshot.artifacts.map((a: any) => (
                  <div key={a.fileName} style={{ marginTop: 8 }}>
                    <div>{a.fileName}</div>
                    <Typography.Text
                      copyable
                      style={{ wordBreak: "break-all" }}
                    >
                      SHA256：{a.sha256}
                    </Typography.Text>
                  </div>
                ))}
              </Card>
            )}
            <Typography.Title level={5}>构建成品</Typography.Title>
            {(detail.artifacts || []).map((a: any) => (
              <Card key={a.id} size="small" title={a.fileName}>
                <div>
                  大小：{(Number(a.fileSize) / 1024 / 1024).toFixed(2)} MB
                </div>
                <Typography.Text copyable style={{ wordBreak: "break-all" }}>
                  SHA256：{a.sha256}
                </Typography.Text>
                <div>
                  {a.availability === "deleted" ? (
                    <Typography.Text type="secondary">
                      历史成品已自动清理
                    </Typography.Text>
                  ) : a.downloadUrl ? (
                    <Button
                      type="link"
                      href={a.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      下载
                    </Button>
                  ) : (
                    <Typography.Text type="secondary">
                      下载地址尚未配置
                    </Typography.Text>
                  )}
                </div>
              </Card>
            ))}
            <Typography.Title level={5}>
              执行日志{" "}
              <Button size="small" onClick={refreshDetail}>
                刷新
              </Button>
            </Typography.Title>
            <pre
              style={{
                background: "#141414",
                color: "#d9f7be",
                padding: 16,
                maxHeight: 380,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              {log || "等待日志…"}
            </pre>
            <Typography.Title level={5}>任务事件</Typography.Title>
            {(detail.events || []).map((e: any) => (
              <div key={e.id}>
                {date(e.createdAt)} · {e.message}
              </div>
            ))}
          </Space>
        )}
      </Drawer>
    </Space>
  );
};
export default BuildJobs;
